import { computed, nextTick, ref, shallowRef } from 'vue';
import type { RouteRecordRaw } from 'vue-router';
import { defineStore } from 'pinia';
import { useRouter } from 'vue-router';
import type { RouteKey } from '@elegant-router/types';
import { SetupStoreId } from '@/store/plugins';

export const useRouteStore = defineStore(SetupStoreId.Route, () => {
  const router = useRouter();
  
  /** 权限路由模式 */
  const authRouteMode = ref('dynamic'); // 默认使用动态路由模式

  /** 首页路由键 */
  const routeHome = ref('root');

  /** 是否已初始化常量路由 */
  const isInitConstantRoute = ref(false);

  /** 是否已初始化权限路由 */
  const isInitAuthRoute = ref(false);

  /**
   * 设置首页路由
   * @param routeKey 路由键
   */
  function setRouteHome(routeKey: RouteKey) {
    routeHome.value = routeKey;
  }

  /** 常量路由 */
  const constantRoutes = shallowRef<any[]>([]);

  function addConstantRoutes(routes: any[]) {
    const constantRoutesMap = new Map<string, any>([]);

    routes.forEach(route => {
      constantRoutesMap.set(route.name, route);
    });

    constantRoutes.value = Array.from(constantRoutesMap.values());
  }

  /** 初始化常量路由 */
  async function initConstantRoute() {
    // 获取常量路由
    const routes = router.getRoutes().filter(route => route.meta?.constant);
    
    // 添加常量路由
    addConstantRoutes(routes);
    
    // 标记常量路由已初始化
    isInitConstantRoute.value = true;
  }

  /** 权限路由 */
  const authRoutes = shallowRef<any[]>([]);

  function addAuthRoutes(routes: any[]) {
    const authRoutesMap = new Map<string, any>([]);

    routes.forEach(route => {
      authRoutesMap.set(route.name, route);
    });

    authRoutes.value = Array.from(authRoutesMap.values());
  }

  /** 初始化权限路由 */
  async function initAuthRoute() {
    // 获取非常量路由作为权限路由
    const routes = router.getRoutes().filter(route => !route.meta?.constant);
    
    // 添加权限路由
    addAuthRoutes(routes);
    
    // 处理常量和权限路由
    handleConstantAndAuthRoutes();
    
    // 标记权限路由已初始化
    isInitAuthRoute.value = true;
  }

  /** 路由切换时的处理（已登录） */
  function onRouteSwitchWhenLoggedIn() {
    // 可以添加路由切换时的处理逻辑
  }

  /** 路由切换时的处理（未登录） */
  function onRouteSwitchWhenNotLoggedIn() {
    // 可以添加路由切换时的处理逻辑
  }

  /** 检查权限路由是否存在 */
  async function getIsAuthRouteExist(path: string) {
    return router.getRoutes().some(route => route.path === path);
  }

  const removeRouteFns: (() => void)[] = [];

  /** 全局菜单 */
  const menus = ref<any[]>([]);
  const searchMenus = computed(() => transformMenuToSearchMenus(menus.value));

  /** 获取全局菜单 */
  function getGlobalMenus(routes: any[]) {
    menus.value = getGlobalMenusByAuthRoutes(routes);
  }

  /** 根据locale更新全局菜单 */
  function updateGlobalMenusByLocale() {
    menus.value = updateLocaleOfGlobalMenus(menus.value);
  }

  /** 缓存路由 */
  const cacheRoutes = ref<RouteKey[]>([]);

  /** 排除缓存路由 */
  const excludeCacheRoutes = ref<RouteKey[]>([]);

  /**
   * 获取缓存路由
   * @param routes Vue路由
   */
  function getCacheRoutes(routes: RouteRecordRaw[]) {
    cacheRoutes.value = getCacheRouteNames(routes);
  }

  /**
   * 重置路由缓存
   * @param routeKey 路由键
   */
  async function resetRouteCache(routeKey?: RouteKey) {
    const routeName = routeKey || (router.currentRoute.value.name as RouteKey);

    excludeCacheRoutes.value.push(routeName);

    await nextTick();

    excludeCacheRoutes.value = [];
  }

  /** 全局面包屑 */
  const breadcrumbs = computed(() => getBreadcrumbsByRoute(router.currentRoute.value, menus.value));

  /** 重置store */
  async function resetStore() {
    const routeStore = useRouteStore();

    routeStore.$reset();

    resetVueRoutes();
  }

  /** 重置Vue路由 */
  function resetVueRoutes() {
    removeRouteFns.forEach(fn => fn());
    removeRouteFns.length = 0;
  }

  /** 处理常量和权限路由 */
  function handleConstantAndAuthRoutes() {
    const allRoutes = [...constantRoutes.value, ...authRoutes.value];

    // 这里可以添加排序和处理逻辑
    getGlobalMenus(allRoutes);
  }

  // 以下是一些辅助函数的模拟实现
  function transformMenuToSearchMenus(menus: any[]) {
    return [];
  }

  function getGlobalMenusByAuthRoutes(routes: any[]) {
    return [];
  }

  function updateLocaleOfGlobalMenus(menus: any[]) {
    return menus;
  }

  function getCacheRouteNames(routes: RouteRecordRaw[]) {
    return [];
  }

  function getBreadcrumbsByRoute(route: any, menus: any[]) {
    return [];
  }

  return {
    authRouteMode,
    routeHome,
    isInitConstantRoute,
    isInitAuthRoute,
    setRouteHome,
    constantRoutes,
    authRoutes,
    menus,
    searchMenus,
    cacheRoutes,
    excludeCacheRoutes,
    breadcrumbs,
    getGlobalMenus,
    updateGlobalMenusByLocale,
    getCacheRoutes,
    resetRouteCache,
    resetStore,
    addConstantRoutes,
    addAuthRoutes,
    initConstantRoute,
    initAuthRoute,
    onRouteSwitchWhenLoggedIn,
    onRouteSwitchWhenNotLoggedIn,
    getIsAuthRouteExist,
    handleConstantAndAuthRoutes
  };
}); 